// src/pages/ChangePassword.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, XCircle, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import api from '../utils/apiClient';
import { validatePasswordStrength } from '../utils/passwordValidation';

// Animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const ParticlesWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const ChangePasswordContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.background};
`;

const ContentCard = styled(motion.div)`
  max-width: 500px;
  width: 100%;
  padding: ${({ theme }) => theme.spacing[8]};
  background: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  backdrop-filter: blur(20px);
  box-shadow: ${({ theme }) => theme.shadows.large};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: white;
  font-size: 2rem;
  animation: ${pulse} 2s infinite;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.primary};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  padding-right: ${({ hasIcon }) => hasIcon ? '3rem' : theme => theme.spacing[4]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border: 1px solid ${({ theme, error }) => error ? theme.colors.error : theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme, error }) => error ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme, error }) => error ? 'rgba(239, 68, 68, 0.2)' : theme.colors.glow};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing[3]};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[1]};
  
  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const PasswordRequirements = styled.div`
  margin-top: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: rgba(0, 210, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid rgba(0, 210, 255, 0.1);
  animation: ${fadeIn} 0.3s ease;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ met, theme }) => met ? theme.colors.success : theme.colors.textMuted};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  animation: ${fadeIn} 0.3s ease;
`;

const SuccessMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.success};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  animation: ${fadeIn} 0.3s ease;
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled(motion.button)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  transition: all 0.3s ease;
  margin-top: ${({ theme }) => theme.spacing[4]};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }

  &:active {
    transform: translateY(0);
  }
`;

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const isFromLogin = location.state?.fromLogin || false;
  const message = location.state?.message || 'Please update your password for security';

  console.log('ChangePassword component state:', {
    isFromLogin,
    locationState: location.state,
    user: !!user
  });

  // Initialize particles
  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = {
    background: {
      color: {
        value: "transparent",
      },
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onHover: {
          enable: true,
          mode: "repulse",
        },
        resize: true,
      },
      modes: {
        repulse: {
          distance: 100,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: "#00d2ff",
      },
      links: {
        color: "#00d2ff",
        distance: 150,
        enable: true,
        opacity: 0.2,
        width: 1,
      },
      collisions: {
        enable: false,
      },
      move: {
        directions: "none",
        enable: true,
        outModes: {
          default: "bounce",
        },
        random: false,
        speed: 1,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 800,
        },
        value: 50,
      },
      opacity: {
        value: 0.3,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 1, max: 3 },
      },
    },
    detectRetina: true,
  };

  useEffect(() => {
    // Redirect if user is not logged in (unless coming from login)
    if (!user && !isFromLogin) {
      navigate('/auth?tab=login');
    }
  }, [user, isFromLogin, navigate]);

  const validatePassword = (password) => {
    return validatePasswordStrength(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate new password
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setError('Password must meet all requirements below');
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // For mandatory password change from login, current password might not be required
    if (!isFromLogin && !currentPassword.trim()) {
      setError('Current password is required');
      return;
    }

    setLoading(true);

    try {
      // Always use the auth changePassword endpoint - it handles both scenarios
      const response = await api.auth.changePassword({
        current_password: currentPassword || '', // Include current password if provided
        new_password: newPassword,
        from_login: isFromLogin // This flag tells backend whether current password is required
      });

      setSuccess('Password changed successfully!');
      toast.success('Password changed successfully!');

      // Redirect after success
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);

    } catch (err) {
      console.error('Password change error:', err);
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'An error occurred while changing password';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordValidation = validatePassword(newPassword);

  return (
    <>
      <ParticlesWrapper>
        <Particles
          id="change-password-particles"
          init={particlesInit}
          options={particlesOptions}
        />
      </ParticlesWrapper>

      <ChangePasswordContainer>
        <ContentCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Header>
            <IconWrapper>
              <Shield />
            </IconWrapper>
            <Title>{isFromLogin ? 'Set New Password' : 'Change Password'}</Title>
            <Subtitle>
              {isFromLogin
                ? 'For security reasons, please set a new password before continuing'
                : message
              }
            </Subtitle>
          </Header>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          {/* {isFromLogin && (
            <SuccessMessage style={{ background: 'rgba(59, 130, 246, 0.1)', borderColor: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' }}>
              <strong>Password Reset Required:</strong> You're setting a new password. No current password needed.
            </SuccessMessage>
          )} */}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="currentPassword">
                {isFromLogin ? 'Current Password' : 'Current Password'}
              </Label>
              <InputWrapper>
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  id="currentPassword"
                  placeholder={isFromLogin ? "Leave empty if setting for first time" : "Enter your current password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required={!isFromLogin}
                  disabled={loading}
                  hasIcon
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </ToggleButton>
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="newPassword">New Password</Label>
              <InputWrapper>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  hasIcon
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </ToggleButton>
              </InputWrapper>

              {newPassword && (
                <PasswordRequirements>
                  <RequirementItem met={passwordValidation.minLength}>
                    {passwordValidation.minLength ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    At least 8 characters
                  </RequirementItem>
                  <RequirementItem met={passwordValidation.hasUpper}>
                    {passwordValidation.hasUpper ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    One uppercase letter
                  </RequirementItem>
                  <RequirementItem met={passwordValidation.hasLower}>
                    {passwordValidation.hasLower ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    One lowercase letter
                  </RequirementItem>
                  <RequirementItem met={passwordValidation.hasNumber}>
                    {passwordValidation.hasNumber ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    One number
                  </RequirementItem>
                  <RequirementItem met={passwordValidation.hasSpecial}>
                    {passwordValidation.hasSpecial ? <CheckCircle size={14} /> : <XCircle size={14} />}
                    One special character (!@#$%^&*...)
                  </RequirementItem>
                </PasswordRequirements>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <InputWrapper>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  error={confirmPassword && newPassword !== confirmPassword}
                  hasIcon
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </ToggleButton>
              </InputWrapper>
              {confirmPassword && newPassword !== confirmPassword && (
                <ErrorMessage>Passwords do not match</ErrorMessage>
              )}
            </FormGroup>

            <SubmitButton
              type="submit"
              disabled={loading || !passwordValidation.isValid || newPassword !== confirmPassword}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              <Lock size={18} />
              {loading ? 'Changing Password...' : 'Change Password'}
            </SubmitButton>

            {!isFromLogin && (
              <BackButton
                type="button"
                onClick={() => navigate('/')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ArrowLeft size={18} />
                Back to Dashboard
              </BackButton>
            )}
          </Form>
        </ContentCard>
      </ChangePasswordContainer>
    </>
  );
};

export default ChangePassword;
